import { useState, type FormEvent } from "react";
import { Navigate, useNavigate } from "react-router";
import { post } from "../../api/client";
import type { Session } from "../../api/types";
import { useSession, useSetSession } from "../../auth/session";
import { Button } from "../../components/ui/Button";
import { TextField } from "../../components/ui/Field";
import { InlineError, Loading } from "../../components/ui/misc";
import { AuthLayout } from "./AuthLayout";

export function MfaPage() {
  const { data: session, isLoading } = useSession();
  const setSession = useSetSession();
  const navigate = useNavigate();
  const [code, setCode] = useState("");
  const [useRecovery, setUseRecovery] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  if (isLoading) return <Loading />;
  if (!session) return <Navigate to="/login" replace />;
  if (!session.mfaPending) return <Navigate to="/phone" replace />;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const s = await post<Session>("/auth/mfa/verify", { code });
      setSession(s);
      navigate("/phone", { replace: true });
    } catch (err) {
      setError(err);
      setCode("");
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout title="Two-step verification" subtitle={useRecovery ? "Enter one of your recovery codes. Each code works once." : "Enter the 6-digit code from your authenticator app."}>
      <form onSubmit={submit} className="space-y-4">
        <TextField
          key={useRecovery ? "recovery" : "totp"}
          label={useRecovery ? "Recovery code" : "Authentication code"}
          inputMode={useRecovery ? "text" : "numeric"}
          autoComplete="one-time-code"
          pattern={useRecovery ? undefined : "\\d{6}"}
          maxLength={useRecovery ? 20 : 6}
          value={code}
          onChange={(e) => setCode(e.target.value)}
          required
          autoFocus
        />
        <InlineError error={error} />
        <Button type="submit" variant="primary" className="w-full" loading={busy}>Verify</Button>
        <button type="button" className="focus-ring w-full rounded text-sm text-accent-600 hover:underline" onClick={() => { setUseRecovery((u) => !u); setCode(""); setError(null); }}>
          {useRecovery ? "Use authenticator code instead" : "Use a recovery code"}
        </button>
        <button type="button" className="focus-ring w-full rounded text-sm text-muted hover:underline"
          onClick={async () => { await post("/auth/logout").catch(() => undefined); setSession(null); navigate("/login"); }}>
          Cancel and sign out
        </button>
      </form>
    </AuthLayout>
  );
}
